/**
 * AssignWorkOrderModal
 * Multi-assignee: a work order can carry any number of internal assignees and
 * external delegates at once. Adding or removing one never disturbs the others.
 * Each external delegate opens the work order with their personal guest link
 * (state shown per row); the composer sets the link's validity for a user
 * without one. No destructive "this replaces the current recipient" gate.
 */
import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner, Badge } from 'react-bootstrap';
import { X } from 'lucide-react';
import CreateExternalUserForm from './CreateExternalUserForm';
import AssignmentPicker, { AssignmentTab } from './AssignmentPicker';
import GuestLinkField from './GuestLinkField';
import AssignmentEmailComposer from './AssignmentEmailComposer';
import CopyGuestLinkButton from './CopyGuestLinkButton';
import ConfirmationModal from './ConfirmationModal';
import { workOrdersApi } from '../api/workOrdersApi';
import { WorkOrderWithBuilding, WorkOrderStatus, getAssignees, getDelegations } from '../types/workOrders';
import { WORK_ORDER_LABELS } from '../Pages/WorkOrders/workOrderSections';
import { Userprofile } from '../Helpers/UsersContext';
import { Delegate, ExternalUser } from '../types/guestAccess';
import { useAssignmentEmailOptions } from '../hooks/useAssignmentEmailOptions';
import { useExternalLink } from '../hooks/useExternalLink';
import { useLinkValidityChoice } from '../hooks/useLinkValidityChoice';
import { defaultAssignmentMessage, defaultAssignmentSubject } from '../utils/assignmentEmail';
import { copyableLinkUrl, describeLinkState, suggestLinkValidity } from '../utils/linkValidity';
import { useDelegates } from '../hooks/useDelegates';
import { useIsAdmin } from '../hooks/useIsAdmin';
import { useComposerReminders } from '../hooks/workOrders/useComposerReminders';
import ComposerRemindersBlock from './WorkOrderReminders/ComposerRemindersBlock';

type Step = 'main' | 'create-user';

interface AssignWorkOrderModalProps {
    show: boolean;
    onHide: () => void;
    workOrder: WorkOrderWithBuilding;
    internalUsers: Userprofile[];
    /** External users, injected by the parent to avoid a redundant fetch in the selector */
    externalUsers?: ExternalUser[];
    /**
     * Current delegates with link state (useDelegates), keyed by externalUserId.
     * Absent (the quick "Tilldela" on Förvaltning and the project board), the
     * modal fetches them itself.
     */
    delegatesByUid?: Record<string, Delegate>;
    onAssigned: () => void;
    /** Called after a new external user is created, so the parent can refresh its user list */
    onExternalUserCreated?: () => void;
}

const AssignWorkOrderModal: React.FC<AssignWorkOrderModalProps> = ({
    show,
    onHide,
    workOrder,
    internalUsers,
    externalUsers,
    delegatesByUid: delegatesByUidProp,
    onAssigned,
    onExternalUserCreated,
}) => {
    const [step, setStep] = useState<Step>('main');
    const [activeTab, setActiveTab] = useState<AssignmentTab>('internal');
    const [selectedInternalUser, setSelectedInternalUser] = useState('');
    const [selectedExternalUser, setSelectedExternalUser] = useState<ExternalUser | null>(null);
    // Whether to email a newly-added internal assignee. On by default.
    const [sendEmail, setSendEmail] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    // The link from the latest external invite; shown until the modal closes.
    const [invitedLink, setInvitedLink] = useState<{ link: string; email?: string; emailSent: boolean } | null>(null);
    const isOpen = workOrder.status === WorkOrderStatus.OPEN;
    // Removal (internal or external) is confirmed before it runs.
    const [pendingRemoval, setPendingRemoval] = useState<
        | { kind: 'internal'; uid: string; name: string }
        | { kind: 'external'; uid: string; name: string }
        | null
    >(null);

    // Current assignees/delegates (read via the normalizer, tolerant of legacy).
    const assignees = getAssignees(workOrder);
    const delegations = getDelegations(workOrder);
    const assigneeUids = Object.keys(assignees);
    const delegateUids = Object.keys(delegations);

    const isAdmin = useIsAdmin();
    const ownDelegates = useDelegates('workOrder', workOrder.key, show && isAdmin && !delegatesByUidProp && delegateUids.length > 0);
    const delegatesByUid = delegatesByUidProp ?? ownDelegates.byUid;
    // The parent refetches its own delegates on onAssigned; the modal's own list is refreshed here.
    const delegationsChanged = () => {
        if (!delegatesByUidProp) void ownDelegates.refetch();
        onAssigned();
    };

    const internalName = (uid: string) => {
        const u = internalUsers.find((x) => x.key === uid);
        return u?.name || u?.email || uid;
    };
    const externalName = (uid: string) => {
        const u = externalUsers?.find((x) => x.uid === uid);
        return u?.name || uid;
    };

    // Email options for the external invite (subject, message, reply-to, copy).
    const emailDefaults = {
        subject: defaultAssignmentSubject('workOrder', workOrder.title, workOrder.buildingName),
        message: selectedExternalUser
            ? defaultAssignmentMessage('workOrder', selectedExternalUser.name)
            : '',
    };
    const email = useAssignmentEmailOptions(show, show && activeTab === 'external', emailDefaults);
    // The selected user's personal link and tasks drive the composer's link
    // block: the validity is pre-filled from the latest due date (+14 days).
    const selectedLink = useExternalLink(show && selectedExternalUser ? selectedExternalUser.uid : null);
    const suggestion = suggestLinkValidity(selectedLink.info?.tasks ?? [], { title: workOrder.title, dueDate: workOrder.dueDate });
    const validity = useLinkValidityChoice(selectedLink.info?.link, selectedExternalUser?.uid ?? null, suggestion);
    // The validity sent depends on the user's current link, so nothing is
    // submitted until it is known (a default would overwrite the link's validity).
    const linkKnown = !selectedLink.loading && selectedLink.info !== null;
    // Reminders armed by this delegation (pre-filled from the instance default).
    const reminders = useComposerReminders(show && isOpen && !!selectedExternalUser, workOrder);

    const handleClose = () => {
        if (!submitting) {
            setStep('main');
            setError(null);
            setSelectedInternalUser('');
            setSelectedExternalUser(null);
            setInvitedLink(null);
            onHide();
        }
    };

    const handleUserCreated = (user: ExternalUser) => {
        setSelectedExternalUser(user);
        setStep('main');
        onExternalUserCreated?.();
    };

    const handleAddInternal = async () => {
        if (!selectedInternalUser) return;
        setError(null);
        setSubmitting(true);
        try {
            await workOrdersApi.addAssignee(workOrder.key, selectedInternalUser, sendEmail);
            setSelectedInternalUser('');
            onAssigned();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte tilldela');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveInternal = async (uid: string) => {
        setError(null);
        setSubmitting(true);
        try {
            await workOrdersApi.removeAssignee(workOrder.key, uid);
            onAssigned();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte ta bort tilldelad');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddExternal = async () => {
        if (!selectedExternalUser) return;
        if (reminders.error) {
            setError(reminders.error);
            return;
        }
        setError(null);
        setSubmitting(true);
        try {
            // A due date set in the reminder block is saved first, so the
            // schedule has a date to count from.
            if (reminders.newDueDate) {
                await workOrdersApi.updateDueDate(workOrder.key, reminders.newDueDate);
            }
            const { link, emailSent } = await workOrdersApi.assignToExternal(
                workOrder.key,
                workOrder.buildingKey,
                selectedExternalUser.uid,
                email.options,
                validity.payload,
                reminders.reminders
            );
            setInvitedLink({ link: link.url, email: selectedExternalUser.email, emailSent });
            setSelectedExternalUser(null);
            delegationsChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte skicka inbjudan');
        } finally {
            setSubmitting(false);
        }
    };

    const handleRemoveExternal = async (uid: string) => {
        setError(null);
        setSubmitting(true);
        try {
            await workOrdersApi.revokeDelegation(workOrder.key, uid);
            delegationsChanged();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Kunde inte ta bort extern mottagare');
        } finally {
            setSubmitting(false);
        }
    };

    // Run the pending removal after the user confirms.
    const confirmRemoval = async () => {
        if (!pendingRemoval) return;
        if (pendingRemoval.kind === 'internal') {
            await handleRemoveInternal(pendingRemoval.uid);
        } else {
            await handleRemoveExternal(pendingRemoval.uid);
        }
        setPendingRemoval(null);
    };

    return (
        <>
        <Modal show={show} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    {step === 'create-user' ? 'Lägg till extern användare' : 'Tilldela ärende'}
                </Modal.Title>
            </Modal.Header>

            {step === 'create-user' ? (
                <CreateExternalUserForm
                    onUserCreated={handleUserCreated}
                    onCancel={() => setStep('main')}
                />
            ) : (
                <>
                    <Modal.Body>
                        {error && (
                            <Alert variant="danger" dismissible onClose={() => setError(null)}>
                                {error}
                            </Alert>
                        )}

                        {!isOpen && (
                            <Alert variant="secondary">
                                {workOrder.status === WorkOrderStatus.CANCELLED
                                    ? WORK_ORDER_LABELS.externalLinksCancelled
                                    : WORK_ORDER_LABELS.externalCompletedReadOnly}
                            </Alert>
                        )}

                        {invitedLink && (
                            <Alert variant={invitedLink.emailSent ? 'success' : 'warning'} className="mb-3">
                                <div className="mb-2">
                                    {invitedLink.emailSent
                                        ? <>Inbjudan skickad{invitedLink.email ? <> till <strong>{invitedLink.email}</strong></> : null}.</>
                                        : WORK_ORDER_LABELS.externalEmailNotSent}
                                </div>
                                <GuestLinkField link={invitedLink.link} />
                            </Alert>
                        )}

                        <AssignmentPicker
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                            internalCount={assigneeUids.length}
                            externalCount={delegateUids.length}
                            readOnly={!isOpen}
                            internalUsers={internalUsers}
                            selectedInternalUser={selectedInternalUser}
                            onInternalUserChange={setSelectedInternalUser}
                            alreadyAssignedIds={assigneeUids}
                            internalAction={(
                                <Button
                                    variant="primary"
                                    onClick={handleAddInternal}
                                    disabled={submitting || !selectedInternalUser}
                                >
                                    {submitting ? <Spinner animation="border" size="sm" /> : "Lägg till"}
                                </Button>
                            )}
                            sendEmail={sendEmail}
                            onSendEmailChange={setSendEmail}
                            externalUsers={externalUsers}
                            selectedExternalUser={selectedExternalUser}
                            onExternalUserChange={setSelectedExternalUser}
                            onCreateExternalUser={() => setStep("create-user")}
                            alreadyDelegatedIds={delegateUids}
                            disabled={submitting}
                            currentInternal={(
                                <>
                                    <Form.Label>Tilldelade</Form.Label>
                                    {assigneeUids.length === 0 ? (
                                        <p className="text-muted small">Inga interna mottagare ännu.</p>
                                    ) : (
                                        <div className="d-flex flex-wrap gap-2 mb-3">
                                            {assigneeUids.map((uid) => (
                                                <Badge key={uid} bg="secondary" className="d-flex align-items-center gap-1 p-2">
                                                    {internalName(uid)}
                                                    {isOpen && (
                                                        <X
                                                            size={14}
                                                            role="button"
                                                            aria-label={`Ta bort ${internalName(uid)}`}
                                                            onClick={() => !submitting && setPendingRemoval({ kind: "internal", uid, name: internalName(uid) })}
                                                        />
                                                    )}
                                                </Badge>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                            currentExternal={(
                                <>
                                    <Form.Label>Externa mottagare</Form.Label>
                                    {delegateUids.length === 0 ? (
                                        <p className="text-muted small">Inga externa mottagare ännu.</p>
                                    ) : (
                                        <div className="mb-3">
                                            {delegateUids.map((uid) => (
                                                <div key={uid} className="d-flex align-items-center justify-content-between border rounded p-2 mb-2">
                                                    <span className="d-flex flex-column">
                                                        <span>{externalName(uid)}</span>
                                                        {delegatesByUid[uid] && (
                                                            <small className="text-muted">{describeLinkState(delegatesByUid[uid].link)}</small>
                                                        )}
                                                    </span>
                                                    {isOpen && (
                                                        <div className="d-flex align-items-center gap-2">
                                                            <CopyGuestLinkButton url={copyableLinkUrl(delegatesByUid[uid]?.link)} />
                                                            <Button
                                                                variant="outline-danger"
                                                                size="sm"
                                                                onClick={() => setPendingRemoval({ kind: "external", uid, name: externalName(uid) })}
                                                                disabled={submitting}
                                                            >
                                                                Ta bort
                                                            </Button>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </>
                            )}
                            externalExtra={selectedExternalUser && (
                                <>
                                    <Alert variant="info">
                                        En e-post med länk skickas till {selectedExternalUser.email}
                                    </Alert>
                                    <AssignmentEmailComposer
                                        options={email.options}
                                        onSubjectChange={email.setSubject}
                                        onCustomMessageChange={email.setCustomMessage}
                                        onReplyToChange={email.setReplyTo}
                                        onSendCopyToAdminChange={email.setSendCopyToAdmin}
                                        taskType="workOrder"
                                        taskTitle={workOrder.title}
                                        buildingName={workOrder.buildingName}
                                        loadingDefault={email.loadingDefault}
                                        disabled={submitting}
                                        idSuffix="workorder"
                                        recipientName={selectedExternalUser.name}
                                        taskKey={workOrder.key}
                                        linkInfo={linkKnown ? selectedLink.info : null}
                                        link={validity}
                                        linkLoading={selectedLink.loading}
                                        linkError={linkKnown ? null : selectedLink.error}
                                        onRetryLink={() => { selectedLink.clearError(); void selectedLink.refetch(); }}
                                    />
                                    <ComposerRemindersBlock reminders={reminders} disabled={submitting} idSuffix="workorder" />
                                </>
                            )}
                        />
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={handleClose} disabled={submitting}>
                            Stäng
                        </Button>
                        {activeTab === 'external' && isOpen && (
                            <Button
                                variant="primary"
                                onClick={handleAddExternal}
                                disabled={submitting || !selectedExternalUser || !linkKnown || !validity.complete || reminders.loading || !!reminders.error}
                            >
                                {submitting ? <Spinner animation="border" size="sm" /> : 'Skicka inbjudan'}
                            </Button>
                        )}
                    </Modal.Footer>
                </>
            )}
        </Modal>

        <ConfirmationModal
            show={!!pendingRemoval}
            title={pendingRemoval?.kind === 'external' ? 'Återkalla delegering' : 'Ta bort tilldelning'}
            message={
                pendingRemoval?.kind === 'external'
                    ? `Vill du återkalla åtkomsten för ${pendingRemoval.name}? Ärendet försvinner från deras portal.`
                    : pendingRemoval
                        ? `Vill du ta bort ${pendingRemoval.name} från ärendet?`
                        : ''
            }
            confirmText={pendingRemoval?.kind === 'external' ? 'Återkalla' : 'Ta bort'}
            loading={submitting}
            onConfirm={confirmRemoval}
            onCancel={() => setPendingRemoval(null)}
        />
        </>
    );
};

export default AssignWorkOrderModal;
